import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { clientAPI, accountAPI, usersAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    GlobeAltIcon,
    MapPinIcon,
    TagIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { ComboboxSearch } from '../../components/kibo-ui/combobox/';

const ClientForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId, id } = useParams();
    const actualId = clientId || id;
    const isEditing = !!(actualId && actualId !== 'new');
    
    // Check if we came from a client detail page (check both old /crm/ and new / paths)
    const cameFromDetail = location.state?.from === 'client-detail' || 
                          document.referrer.includes(`/crm/clients/${actualId}`) ||
                          document.referrer.includes(`/clients/${actualId}`) ||
                          (location.state?.clientId && location.state.clientId === actualId);
    
    console.log('👤 ClientForm rendered:', { clientId, id, actualId, isEditing, pathname: window.location.pathname, cameFromDetail });

    const [loading, setLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [agents, setAgents] = useState([]);
    const [formData, setFormData] = useState({
        // Basic Information
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        
        // Location Information
        countryNationality: '',
        countryResidence: '',
        preferredLanguage: '',
        timezone: '',
        
        // Business Information
        accountId: '',
        assignedUsers: [],
        
        // Status and Source
        status: 'prospect',
        leadSource: 'web_form',
        
        // Tags and Notes
        tags: [],
        notes: '',
        
        // Consent
        consent: {
            email: false,
            sms: false,
            lawfulBasis: 'consent'
        }
    });

    const [errors, setErrors] = useState({});

    const statusOptions = [
        { value: 'prospect', label: 'Prospect', color: 'yellow' },
        { value: 'active', label: 'Active', color: 'green' },
        { value: 'paused', label: 'Paused', color: 'blue' },
        { value: 'churned', label: 'Churned', color: 'red' },
        { value: 'closed_lost', label: 'Closed Lost', color: 'gray' }
    ];

    const leadSourceOptions = [
        { value: 'web_form', label: 'Web Form' },
        { value: 'referral', label: 'Referral' },
        { value: 'social_media', label: 'Social Media' },
        { value: 'email_campaign', label: 'Email Campaign' },
        { value: 'phone_call', label: 'Phone Call' },
        { value: 'walk_in', label: 'Walk In' },
        { value: 'other', label: 'Other' }
    ];

    const countryOptions = [
        { value: 'ES', label: 'Spain' },
        { value: 'FR', label: 'France' },
        { value: 'DE', label: 'Germany' },
        { value: 'IT', label: 'Italy' },
        { value: 'GB', label: 'United Kingdom' },
        { value: 'US', label: 'United States' },
        { value: 'CA', label: 'Canada' },
        { value: 'AU', label: 'Australia' },
        { value: 'NL', label: 'Netherlands' },
        { value: 'BE', label: 'Belgium' },
        { value: 'CH', label: 'Switzerland' },
        { value: 'AT', label: 'Austria' },
        { value: 'SE', label: 'Sweden' },
        { value: 'NO', label: 'Norway' },
        { value: 'DK', label: 'Denmark' },
        { value: 'FI', label: 'Finland' },
        { value: 'PT', label: 'Portugal' },
        { value: 'IE', label: 'Ireland' },
        { value: 'LU', label: 'Luxembourg' },
        { value: 'MT', label: 'Malta' },
        { value: 'CY', label: 'Cyprus' },
        { value: 'GR', label: 'Greece' },
        { value: 'PL', label: 'Poland' },
        { value: 'CZ', label: 'Czech Republic' },
        { value: 'HU', label: 'Hungary' },
        { value: 'SK', label: 'Slovakia' },
        { value: 'SI', label: 'Slovenia' },
        { value: 'HR', label: 'Croatia' },
        { value: 'RO', label: 'Romania' },
        { value: 'BG', label: 'Bulgaria' },
        { value: 'LT', label: 'Lithuania' },
        { value: 'LV', label: 'Latvia' },
        { value: 'EE', label: 'Estonia' }
    ];

    const languageOptions = [
        { value: 'es', label: 'Spanish' },
        { value: 'en', label: 'English' },
        { value: 'fr', label: 'French' },
        { value: 'de', label: 'German' },
        { value: 'it', label: 'Italian' },
        { value: 'pt', label: 'Portuguese' },
        { value: 'nl', label: 'Dutch' },
        { value: 'sv', label: 'Swedish' },
        { value: 'no', label: 'Norwegian' },
        { value: 'da', label: 'Danish' },
        { value: 'fi', label: 'Finnish' },
        { value: 'pl', label: 'Polish' },
        { value: 'cs', label: 'Czech' },
        { value: 'hu', label: 'Hungarian' },
        { value: 'sk', label: 'Slovak' },
        { value: 'sl', label: 'Slovenian' },
        { value: 'hr', label: 'Croatian' },
        { value: 'ro', label: 'Romanian' },
        { value: 'bg', label: 'Bulgarian' },
        { value: 'lt', label: 'Lithuanian' },
        { value: 'lv', label: 'Latvian' },
        { value: 'et', label: 'Estonian' },
        { value: 'el', label: 'Greek' },
        { value: 'mt', label: 'Maltese' },
        { value: 'cy', label: 'Welsh' },
        { value: 'ga', label: 'Irish' },
        { value: 'eu', label: 'Basque' },
        { value: 'ca', label: 'Catalan' },
        { value: 'gl', label: 'Galician' }
    ];

    const timezoneOptions = [
        { value: 'Europe/Madrid', label: 'Europe/Madrid' },
        { value: 'Europe/London', label: 'Europe/London' },
        { value: 'Europe/Paris', label: 'Europe/Paris' },
        { value: 'Europe/Berlin', label: 'Europe/Berlin' },
        { value: 'America/New_York', label: 'America/New_York' },
        { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
        { value: 'America/Chicago', label: 'America/Chicago' },
        { value: 'America/Denver', label: 'America/Denver' },
        { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
        { value: 'Australia/Sydney', label: 'Australia/Sydney' }
    ];

    useEffect(() => {
        fetchAccounts();
        fetchAgents();
        if (isEditing) {
            fetchClient();
        }
    }, [actualId]);

    const fetchAccounts = async () => {
        try {
            const response = await accountAPI.getAll({ limit: 100 });
            if (response.data.success) {
                setAccounts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const fetchAgents = async () => {
        try {
            const response = await usersAPI.getAgents();
            if (response.data.success) {
                setAgents(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching agents:', error);
        }
    };

    const fetchClient = async () => {
        try {
            setLoading(true);
            const response = await clientAPI.getById(actualId);
            if (response.data.success) {
                const client = response.data.data;
                // Use firstName/lastName directly, with fallback to name for backward compatibility
                const firstName = client.firstName || (client.name ? client.name.split(' ')[0] : '');
                const lastName = client.lastName || (client.name ? client.name.split(' ').slice(1).join(' ') : '');
                
                setFormData({
                    firstName: firstName,
                    lastName: lastName,
                    email: client.email || '',
                    phone: client.phoneE164 || client.phone || '',
                    countryNationality: client.countryNationality || '',
                    countryResidence: client.countryResidence || '',
                    preferredLanguage: client.preferredLanguage || '',
                    timezone: client.timezone || '',
                    accountId: client.accountId?._id || '',
                    clientPartnerUserId: client.clientPartnerUserId || '',
                    status: client.status || 'prospect',
                    leadSource: client.leadSource || 'web_form',
                    tags: client.tags || [],
                    notes: client.notes || '',
                    consent: {
                        email: client.consent?.email || false,
                        sms: client.consent?.sms || false,
                        lawfulBasis: client.consent?.lawfulBasis || 'consent'
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching client:', error);
            toast.error('Failed to load client data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name.startsWith('consent.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                consent: {
                    ...prev.consent,
                    [field]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleTagAdd = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const tag = e.target.value.trim();
            if (tag && !formData.tags.includes(tag)) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, tag]
                }));
                e.target.value = '';
            }
        }
    };

    const handleTagRemove = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (formData.phone && !/^\+[1-9]\d{1,14}$/.test(formData.phone)) {
            newErrors.phone = 'Phone must be in E.164 format (e.g., +1234567890)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        try {
            setLoading(true);
            
            const clientData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneE164: formData.phone ? (formData.phone.startsWith('+') ? formData.phone : `+${formData.phone}`) : undefined,
                countryNationality: formData.countryNationality || undefined,
                countryResidence: formData.countryResidence || undefined,
                preferredLanguage: formData.preferredLanguage || undefined,
                timezone: formData.timezone || undefined,
                accountId: formData.accountId || undefined,
                status: formData.status,
                leadSource: formData.leadSource,
                tags: formData.tags || [],
                consent: formData.consent || { email: false, sms: false, lawfulBasis: 'consent' },
                notes: formData.notes || ''
            };

            let response;
            if (isEditing) {
                response = await clientAPI.update(actualId, clientData);
            } else {
                response = await clientAPI.create(clientData);
            }

            if (response.data.success) {
                toast.success(`Client ${isEditing ? 'updated' : 'created'} successfully!`);
                // If editing and came from client detail, go back to detail view
                if (isEditing && cameFromDetail) {
                    navigate(`/clients/${actualId}`);
                } else {
                    navigate('/clients');
                }
            } else {
                throw new Error(response.data.message || 'Failed to save client');
            }
        } catch (error) {
            console.error('Error saving client:', error);
            toast.error(error.message || 'Failed to save client');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditing) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border mb-6">
                <div className="px-6 py-4 border-b border-border">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/clients')}
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <UserIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-foreground">
                                    {isEditing ? 'Edit Client' : 'Add New Client'}
                                </h1>
                                <p className="text-muted-foreground">
                                    {isEditing ? 'Update client information' : 'Create a new client record'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors.firstName ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="Enter first name"
                                />
                                {errors.firstName && (
                                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors.lastName ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="Enter last name"
                                />
                                {errors.lastName && (
                                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Email Address *
                                </label>
                                <div className="relative">
                                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                            errors.email ? 'border-red-300' : 'border-input'
                                        }`}
                                        placeholder="client@example.com"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Phone Number
                                </label>
                                <div className="relative">
                                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                            errors.phone ? 'border-red-300' : 'border-input'
                                        }`}
                                        placeholder="+1234567890"
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                                )}
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Include country code (e.g., +34 for Spain)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Status
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Assigned Partner
                                </label>
                                <select
                                    name="clientPartnerUserId"
                                    value={formData.clientPartnerUserId || ''}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="">Select a partner (optional)</option>
                                    {agents.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.name} ({user.email})
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-muted-foreground">Agency admin or user who is the primary contact.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Location Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Location Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Country of Nationality
                                </label>
                                <select
                                    name="countryNationality"
                                    value={formData.countryNationality || ''}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="">Select a country...</option>
                                    {countryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Country of Residence
                                </label>
                                <select
                                    name="countryResidence"
                                    value={formData.countryResidence || ''}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="">Select a country...</option>
                                    {countryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Preferred Language
                                </label>
                                <select
                                    name="preferredLanguage"
                                    value={formData.preferredLanguage}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    {languageOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Timezone
                                </label>
                                <select
                                    name="timezone"
                                    value={formData.timezone}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    {timezoneOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Business Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Business Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Associated Account
                                </label>
                                <ComboboxSearch
                                    data={[
                                        { value: '', label: 'Select an account (optional)' },
                                        ...accounts.map(account => ({
                                            value: account._id,
                                            label: account.name
                                        }))
                                    ]}
                                    value={formData.accountId}
                                    onValueChange={(value) => setFormData(prev => ({
                                        ...prev,
                                        accountId: value
                                    }))}
                                    placeholder="Search and select account..."
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Lead Source
                                </label>
                                <select
                                    name="leadSource"
                                    value={formData.leadSource}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    {leadSourceOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tags */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Tags</h3>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 mb-4">
                                {formData.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                    >
                                        <TagIcon className="h-4 w-4" />
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleTagRemove(tag)}
                                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
                                        >
                                            <XMarkIcon className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <input
                                type="text"
                                placeholder="Add a tag and press Enter"
                                onKeyPress={handleTagAdd}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            />
                        </div>
                    </div>
                </div>

                {/* Consent */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Consent & Communication</h3>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="space-y-3">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="consent.email"
                                    checked={formData.consent.email}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                />
                                <span className="ml-2 text-sm text-foreground">
                                    Email communications
                                </span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="consent.sms"
                                    checked={formData.consent.sms}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                />
                                <span className="ml-2 text-sm text-foreground">
                                    SMS communications
                                </span>
                            </label>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Lawful Basis
                            </label>
                            <select
                                name="consent.lawfulBasis"
                                value={formData.consent.lawfulBasis}
                                onChange={handleInputChange}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            >
                                <option value="consent">Consent</option>
                                <option value="contract">Contract</option>
                                <option value="legitimate_interest">Legitimate Interest</option>
                                <option value="vital_interest">Vital Interest</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end gap-3 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/clients')}
                        className="px-6 py-2 text-sm font-medium text-foreground bg-card border border-input bg-background text-foreground rounded-lg hover:bg-muted focus:outline-none focus:ring-2 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {isEditing ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <CheckCircleIcon className="h-4 w-4" />
                                {isEditing ? 'Update Client' : 'Create Client'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ClientForm;